import { IHeadTag } from '../../../../interfaces/IFrontPage';
import Head from 'next/head';
import React from 'react';
import SeoSchema from '../../../../src/components/sections/seo-schema/seo-schema';
import { getHomePageSeoSchema } from '../../../pages-controller/home-page/seo-schema';
import {
    getBreadCrumbSeoSchema,
    getProductSeoSchema,
    getGoogleEventSchema,
    getGoogleRatingSchema,
} from '../../../../src/pages-controller/landing-page/seo-schema';
import { getBreadcrumbsPath } from '../landing-breadcrumbs/landing-breadcrumbs';
import { getMetaTags } from './head-tags.data';
import { LocaleConfigContext } from '../../../contexts/locale-config/locale-config';
import { SHIP_PAGE_TYPES, SHIP_SUBPAGE_TYPES } from '../../../../src/constants/shipLanding';
import { generateCruiseUrl } from '../../../utils/url-generation';

const getGoogleCruiseEventSchema = (cruise: any, baseUrl: string) => {
    const formattedRegion = cruise.region?.titles?.join(',');
    const cruiseData = {
        title: cruise.title,
        earliestDeparture: cruise.sailings?.earliestDepartureDate,
        latestArrival: cruise.sailings?.latestReturnDate,
        region: formattedRegion,
        url: baseUrl,
    };

    return <SeoSchema key={cruise.cruiseId} seoData={getGoogleEventSchema(cruiseData)} />;
};

export const HeadTags = (props: IHeadTag) => {
    const { hostUrl, landingPage, modulesData, pageType } = props;
    const reviews = modulesData?.ugcData;
    const localeConfig = React.useContext(LocaleConfigContext);
    const meta = getMetaTags(props, localeConfig);

    const allowedSubpageTypes = [SHIP_SUBPAGE_TYPES.POSITION, SHIP_SUBPAGE_TYPES.CABIN];
    const isShipDetailPage =
        landingPage.type === SHIP_PAGE_TYPES.SHIP && !landingPage.subpage.status;
    const isAllowedSubpage =
        landingPage.subpage.status && allowedSubpageTypes.includes(landingPage.subpage.type || '');
    const hasGoogleEventSchema = isShipDetailPage || isAllowedSubpage;

    const cruises = modulesData.cruiseSearchlandingpageOffer?.items || [];
    const topThreeEventCruises = cruises?.length >= 3 ? cruises.slice(0, 3) : [];
    const isReviewsPage = landingPage.subpage.type === SHIP_SUBPAGE_TYPES.UGC;

    const ratingData = {
        title: landingPage?.title,
        ratingValue: reviews?.aggregation?.data.attributes.overall || 0,
        reviewCount: reviews?.aggregation?.data.attributes.numRatings || 0,
    };

    const getCruiseUrl = (cruise: any) => {
        const cruiseUrl = generateCruiseUrl(
            localeConfig.slugs.cruise.prefix,
            cruise?.title,
            Number(cruise?.cruiseId),
        );
        return hostUrl + cruiseUrl;
    };

    return (
        <>
            <Head>
                <title>{meta?.title}</title>
                <meta name="description" content={meta?.description} />
                <meta property="og:title" content={meta?.title} />
                <meta name="robots" content={meta?.robots || 'index, follow'} />
                <link rel="canonical" href={meta?.canonical || hostUrl} />
                <meta property="og:url" content={(meta?.og?.url as any) || hostUrl} />
                <meta property="og:description" content={meta?.description} />
                <meta property="og:image" content={meta?.og?.image?.url as any} />
                <meta
                    property="og:image:width"
                    content={(meta?.og?.image?.width as any) || '1200'}
                />
                <meta
                    property="og:image:height"
                    content={(meta?.og?.image?.height as any) || '630'}
                />
                <meta property="og:type" content={meta?.og?.type || 'website'} />
                <meta property="og:site_name" content={meta?.og?.siteName || ''} />
                <meta property="og:locale" content={meta?.og?.locale || 'de_DE'} />
                <meta key="charSet" charSet="utf-8" className="next-head" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
                />
            </Head>
            {hostUrl && pageType === 'homePage' ? (
                <SeoSchema seoData={getHomePageSeoSchema(hostUrl, localeConfig)} />
            ) : null}
            {hostUrl && pageType === 'landingPage' ? (
                <>
                    {isShipDetailPage && (
                        <SeoSchema seoData={getProductSeoSchema(modulesData, landingPage)} />
                    )}
                    {isReviewsPage && <SeoSchema seoData={getGoogleRatingSchema(ratingData)} />}
                    <SeoSchema
                        seoData={getBreadCrumbSeoSchema(
                            getBreadcrumbsPath(landingPage, modulesData, localeConfig),
                            hostUrl,
                        )}
                    />
                    {hasGoogleEventSchema &&
                        topThreeEventCruises.map((cruise) =>
                            getGoogleCruiseEventSchema(cruise, getCruiseUrl(cruise)),
                        )}
                </>
            ) : null}
        </>
    );
};
